import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronRight,
  Copy,
  File as FileIcon,
  FileText,
  History,
  Loader2,
  Maximize2,
  MessageSquare,
  Minimize2,
  Paperclip,
  Plus,
  RotateCcw,
  Square,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { deleteConversation, listConversations } from './chat-api';
import { useChat } from '../hooks/use-chat';
import { useDockSize } from '../hooks/use-dock-size';
import { useHealth } from '../hooks/use-health';
import { accentVars } from './accent';
import { Markdown } from './markdown';
import type {
  AssistantConfig,
  ChatMessage,
  ConversationSummary,
  DockPosition,
  FeedbackReason,
  PendingAttachment,
  ToolCall,
} from './types';

/** Below this the dock takes over the screen and position stops mattering. */
const FULLSCREEN_BELOW = 520;

let attachmentSeq = 0;
function nextAttachmentId(): string {
  attachmentSeq += 1;
  return `a${Date.now().toString(36)}${attachmentSeq}`;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A short label for a non-image attachment chip, e.g. "data:application/pdf;..." -> "PDF". */
function labelForMimeType(mimeType: string): string {
  const subtype = mimeType.split('/')[1] ?? 'file';
  return subtype.toUpperCase();
}

/**
 * Tool names arrive in the model's own vocabulary ("create_sales_order",
 * "listVendors", "getAPIKey"), which is not what a user should be reading. Turn
 * it into a sentence-cased phrase.
 */
function labelForTool(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_\-\s]+/g, ' ')
    .trim();
  if (words === '') return 'a tool';
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * What a step reads as: the API's own wording when it sent one, otherwise a
 * phrase made from the name.
 */
function labelForStep(step: ToolCall): string {
  if (step.label) return step.label;
  if (step.kind === 'agent') {
    const agent = labelForTool(step.name).toLowerCase();
    return step.done ? `Asked the ${agent} agent` : `Asking the ${agent} agent`;
  }
  return step.done ? `Used ${labelForTool(step.name)}` : `Using ${labelForTool(step.name)}`;
}

/** "8s", "1m 05s". */
function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/** Whole seconds since the calling component mounted, ticking once a second. */
function useElapsedSeconds(): number {
  const [start] = useState(() => Date.now());
  const [now, setNow] = useState(start);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return Math.floor((now - start) / 1000);
}

/** "26 Sep", or the time for today. */
function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < FULLSCREEN_BELOW,
  );

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${FULLSCREEN_BELOW - 1}px)`);
    const onChange = () => setNarrow(query.matches);
    onChange();
    // Safari < 14 has no addEventListener on MediaQueryList.
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else query.removeListener(onChange);
    };
  }, []);

  return narrow;
}

function anchorStyle(position: DockPosition, offset: number): React.CSSProperties {
  const style: React.CSSProperties = { position: 'fixed' };
  if (position.includes('bottom')) style.bottom = offset;
  else style.top = offset;
  if (position.includes('right')) style.right = offset;
  else style.left = offset;
  return style;
}

/* ------------------------------------------------------------------ *
 * Resize grips
 * ------------------------------------------------------------------ */

function Grips({
  position,
  handleProps,
}: {
  position: DockPosition;
  handleProps: {
    onPointerDown: (axis: 'v' | 'h' | 'c') => (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: () => void;
  };
}) {
  const atBottom = position.includes('bottom');
  const atRight = position.includes('right');

  // Grips sit on the two edges facing away from the anchored corner, so a drag
  // always grows the panel into open space rather than into the wall.
  const vertical: React.CSSProperties = {
    position: 'absolute',
    left: 12,
    right: 12,
    height: 7,
    cursor: 'ns-resize',
    ...(atBottom ? { top: 0 } : { bottom: 0 }),
  };
  const horizontal: React.CSSProperties = {
    position: 'absolute',
    top: 12,
    bottom: 12,
    width: 7,
    cursor: 'ew-resize',
    ...(atRight ? { left: 0 } : { right: 0 }),
  };
  const corner: React.CSSProperties = {
    position: 'absolute',
    width: 16,
    height: 16,
    cursor: atBottom === atRight ? 'nwse-resize' : 'nesw-resize',
    ...(atBottom ? { top: 0 } : { bottom: 0 }),
    ...(atRight ? { left: 0 } : { right: 0 }),
  };

  const shared = {
    onPointerMove: handleProps.onPointerMove,
    onPointerUp: handleProps.onPointerUp,
    className: 'z-30 touch-none',
  };

  return (
    <>
      <div {...shared} style={vertical} onPointerDown={handleProps.onPointerDown('v')} />
      <div {...shared} style={horizontal} onPointerDown={handleProps.onPointerDown('h')} />
      <div {...shared} style={corner} onPointerDown={handleProps.onPointerDown('c')} />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Turns
 * ------------------------------------------------------------------ */

/** How far each step sits under its agent. A step's parent always starts before it, so one pass gives every depth. */
function stepDepths(steps: ToolCall[]): Map<string, number> {
  const depths = new Map<string, number>();
  for (const step of steps) {
    depths.set(step.ref, step.parent === undefined ? 0 : (depths.get(step.parent) ?? -1) + 1);
  }
  return depths;
}

/**
 * A finished turn's steps, folded into one line so the answer leads; opening
 * it shows the same list the turn showed while it ran.
 */
function StepsSummary({ steps }: { steps: ToolCall[] }) {
  const [open, setOpen] = useState(false);

  if (steps.length === 0) return null;

  const depths = stepDepths(steps);

  return (
    <div className="mb-1.5 text-[12.5px]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="-ml-1 flex items-center gap-1 rounded px-1 py-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span>{steps.length === 1 ? '1 step' : `${steps.length} steps`}</span>
        <ChevronRight
          className={cn('size-3.5 shrink-0 transition-transform duration-150', open && 'rotate-90')}
        />
      </button>
      {open ? (
        <ol className="mt-1 flex animate-in flex-col gap-1 duration-150 fade-in-0" aria-label="What the assistant did">
          {steps.map((step) => {
            const depth = depths.get(step.ref) ?? 0;
            return (
              <li
                key={step.ref}
                style={depth > 0 ? { marginLeft: 7 + (depth - 1) * 20 } : undefined}
                className={cn(
                  'flex items-center gap-2 text-muted-foreground',
                  depth > 0 && 'border-l border-border pl-3',
                )}
              >
                <Check className="size-3.5 shrink-0 text-[var(--assistant-accent)]" />
                <span className="min-w-0 truncate" title={step.name}>
                  {labelForStep(step)}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}

/** Text that a highlight sweeps across, for the step happening right now. */
function Shimmer({ children }: { children: React.ReactNode }) {
  return (
    <span className="animate-shimmer bg-[linear-gradient(90deg,hsl(var(--muted-foreground))_35%,hsl(var(--foreground))_50%,hsl(var(--muted-foreground))_65%)] bg-[length:200%_100%] bg-clip-text text-transparent motion-reduce:animate-none">
      {children}
    </span>
  );
}

/**
 * The live list of what the turn is doing, one row per tool or sub-agent in
 * the order they started, ending on a status row for whatever is happening
 * now — so a long turn always shows movement and how long it has taken.
 */
function ActivitySteps({ steps, waiting }: { steps: ToolCall[]; waiting: boolean }) {
  const elapsed = useElapsedSeconds();

  // The newest step still running is the one the user is waiting on; an
  // agent that is open around it is still running too, just not the focus.
  let current: ToolCall | undefined;
  for (const step of steps) if (!step.done) current = step;

  // Between steps, and before the first one, nothing is running: the model
  // itself is working out what to do next.
  const idle = waiting && current === undefined;
  const idleText = steps.length === 0 ? 'Thinking' : 'Reviewing the results';
  const showTimer = waiting && elapsed >= 2;

  if (steps.length === 0 && !waiting) return null;

  const depths = stepDepths(steps);

  return (
    <ol className="mb-1.5 flex flex-col gap-1 text-[12.5px]" aria-label="What the assistant is doing">
      {steps.map((step) => {
        const isCurrent = waiting && step === current;
        const depth = depths.get(step.ref) ?? 0;
        return (
          <li
            key={step.ref}
            // Each level sits one icon-width in, under its agent's icon.
            style={depth > 0 ? { marginLeft: 7 + (depth - 1) * 20 } : undefined}
            className={cn(
              'flex animate-in items-center gap-2 duration-200 fade-in-0 slide-in-from-bottom-1',
              depth > 0 && 'border-l border-border pl-3',
              step.done ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {step.done ? (
              <Check className="size-3.5 shrink-0 text-[var(--assistant-accent)]" />
            ) : (
              <Loader2 className="size-3.5 shrink-0 animate-spin text-[var(--assistant-accent)]" />
            )}
            <span className="min-w-0 truncate" title={step.name}>
              {isCurrent ? <Shimmer>{labelForStep(step)}…</Shimmer> : labelForStep(step)}
            </span>
            {isCurrent && showTimer ? (
              <span className="ml-auto shrink-0 tabular-nums text-[11px] text-muted-foreground">
                {formatElapsed(elapsed)}
              </span>
            ) : null}
          </li>
        );
      })}
      {idle ? (
        <li className="flex animate-in items-center gap-2 duration-200 fade-in-0" role="status">
          <span className="relative grid size-3.5 shrink-0 place-items-center" aria-hidden="true">
            <span className="absolute size-2.5 animate-ping rounded-full bg-[var(--assistant-accent)] opacity-40 motion-reduce:animate-none" />
            <span className="size-1.5 rounded-full bg-[var(--assistant-accent)]" />
          </span>
          <Shimmer>{idleText}…</Shimmer>
          {showTimer ? (
            <span className="ml-auto shrink-0 tabular-nums text-[11px] text-muted-foreground">
              {formatElapsed(elapsed)}
            </span>
          ) : null}
        </li>
      ) : null}
    </ol>
  );
}

function UserTurn({ message }: { message: ChatMessage }) {
  return (
    <div className="flex flex-col items-end gap-1.5">
      {message.attachments && message.attachments.length > 0 ? (
        <div className="flex max-w-[86%] flex-wrap justify-end gap-1.5">
          {message.attachments.map((attachment, index) =>
            attachment.mimeType.startsWith('image/') ? (
              <img
                key={index}
                src={attachment.src}
                alt={attachment.name}
                title={attachment.name}
                className="size-16 rounded-md border border-border object-cover"
              />
            ) : (
              // A stored file opens from the server; one attached in this
              // page is only a data URL, which browsers refuse to open.
              <a
                key={index}
                href={attachment.src.startsWith('data:') ? undefined : attachment.src}
                target="_blank"
                rel="noreferrer"
                title={attachment.name}
                className="flex size-16 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/40 px-1"
              >
                <FileIcon className="size-4 text-muted-foreground" />
                <span className="text-[9px] font-medium text-muted-foreground">
                  {labelForMimeType(attachment.mimeType)}
                </span>
              </a>
            ),
          )}
        </div>
      ) : null}
      {message.content ? (
        <div
          className="max-w-[86%] whitespace-pre-wrap break-words rounded-[14px_14px_4px_14px] bg-[var(--assistant-accent-soft)] px-3 py-2 text-[13.5px] leading-[1.55]"
        >
          {message.content}
        </div>
      ) : null}
    </div>
  );
}

const REASON_LABELS: Record<FeedbackReason, string> = {
  wrong_answer: 'Wrong answer',
  misunderstood: "Didn't understand me",
  incomplete: 'Incomplete',
  other: 'Something else',
};

type RateFn = (rating: 1 | -1 | 0, reasons?: FeedbackReason[], comment?: string | null) => void;

const metaButton =
  'flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function TurnMeta({
  message,
  onRetry,
  canRetry,
  onRate,
  onThumbsDown,
  pinned,
}: {
  message: ChatMessage;
  onRetry: () => void;
  canRetry: boolean;
  /** Absent when this reply cannot be rated. */
  onRate?: RateFn;
  onThumbsDown: () => void;
  /** Keep the row showing rather than only on hover. */
  pinned: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(message.content).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      },
      () => undefined,
    );
  }, [message.content]);

  const usage = message.usage;
  const rating = message.feedback?.rating ?? 0;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 pl-[31px] transition-opacity focus-within:opacity-100 group-hover:opacity-100',
        !pinned && 'opacity-0',
      )}
    >
      {onRate ? (
        <span className="flex items-center">
          <button
            type="button"
            onClick={() => onRate(rating === 1 ? 0 : 1)}
            title="Good response"
            aria-label="Good response"
            aria-pressed={rating === 1}
            className={cn(metaButton, rating === 1 && 'text-[var(--assistant-accent)]')}
          >
            <ThumbsUp className={cn('size-3', rating === 1 && 'fill-current')} />
          </button>
          <button
            type="button"
            onClick={() => {
              if (rating === -1) onRate(0);
              else {
                onRate(-1);
                onThumbsDown();
              }
            }}
            title="Bad response"
            aria-label="Bad response"
            aria-pressed={rating === -1}
            className={cn(metaButton, rating === -1 && 'text-destructive')}
          >
            <ThumbsDown className={cn('size-3', rating === -1 && 'fill-current')} />
          </button>
        </span>
      ) : null}
      {message.requestId ? (
        <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">
          {message.requestId}
        </span>
      ) : null}
      {usage ? (
        <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">
          {(usage.input_tokens ?? 0).toLocaleString()} in · {(usage.output_tokens ?? 0).toLocaleString()} out
        </span>
      ) : null}
      <button
        type="button"
        onClick={copy}
        className={metaButton}
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      {canRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className={metaButton}
        >
          <RotateCcw className="size-3" />
          Retry
        </button>
      ) : null}
    </div>
  );
}

/**
 * Asked after a thumbs down, which is already recorded — this only adds why.
 * Skipping it loses nothing.
 */
function FeedbackDetails({
  onSend,
  onClose,
}: {
  onSend: (reasons: FeedbackReason[], comment: string | null) => void;
  onClose: () => void;
}) {
  const [reasons, setReasons] = useState<FeedbackReason[]>([]);
  const [comment, setComment] = useState('');

  const toggle = (reason: FeedbackReason) =>
    setReasons((prev) => (prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]));

  const canSend = reasons.length > 0 || comment.trim() !== '';

  return (
    <div className="ml-[31px] rounded-md border border-border bg-muted/30 px-3 py-2.5">
      <div className="flex items-center">
        <p className="text-[12px] font-medium text-foreground">What went wrong?</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="ml-auto grid size-5 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-3" />
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(Object.keys(REASON_LABELS) as FeedbackReason[]).map((reason) => (
          <button
            key={reason}
            type="button"
            onClick={() => toggle(reason)}
            aria-pressed={reasons.includes(reason)}
            className={cn(
              'rounded-full border px-2 py-0.5 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              reasons.includes(reason)
                ? 'border-[var(--assistant-accent)] bg-[var(--assistant-accent-soft)] text-foreground'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {REASON_LABELS[reason]}
          </button>
        ))}
      </div>
      <textarea
        rows={2}
        value={comment}
        maxLength={1000}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Tell us more (optional)"
        className="mt-2 block w-full resize-none rounded border border-input bg-background px-2 py-1.5 text-[12px] leading-normal placeholder:text-muted-foreground focus:border-[var(--assistant-accent)] focus:outline-none"
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          disabled={!canSend}
          onClick={() => onSend(reasons, comment.trim() === '' ? null : comment.trim())}
          className="rounded-md bg-[var(--assistant-accent)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--assistant-accent-contrast)] transition-colors hover:bg-[var(--assistant-accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
}

function AssistantTurn({
  message,
  onRetry,
  canRetry,
  onRate,
  isLast,
}: {
  message: ChatMessage;
  onRetry: () => void;
  canRetry: boolean;
  onRate?: RateFn;
  isLast: boolean;
}) {
  const [askWhy, setAskWhy] = useState(false);
  const [thanked, setThanked] = useState(false);

  useEffect(() => {
    if (!thanked) return undefined;
    const id = window.setTimeout(() => setThanked(false), 2400);
    return () => window.clearTimeout(id);
  }, [thanked]);

  // Taking a thumbs down back leaves nothing to explain.
  const showDetails = askWhy && message.feedback?.rating === -1 && onRate !== undefined;

  return (
    <div className="group flex flex-col gap-1.5">
      <div className="flex items-start gap-2.5">
        <div className="mt-px grid size-[22px] shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
          <FileText className="size-[13px]" />
        </div>
        <div className="min-w-0 flex-1">
          <StepsSummary steps={message.tools ?? []} />
          <Markdown source={message.content} />
          {message.stopped ? (
            <p className="mt-1 text-[12px] text-muted-foreground">Stopped.</p>
          ) : null}
          {message.truncated ? (
            <p className="mt-1 text-[12px] text-muted-foreground">
              This reply hit the length limit and may be incomplete.
            </p>
          ) : null}
        </div>
      </div>
      <TurnMeta
        message={message}
        onRetry={onRetry}
        canRetry={canRetry}
        onRate={onRate}
        onThumbsDown={() => setAskWhy(true)}
        pinned={isLast || Boolean(message.feedback) || showDetails}
      />
      {showDetails ? (
        <FeedbackDetails
          onClose={() => setAskWhy(false)}
          onSend={(reasons, comment) => {
            onRate(-1, reasons, comment);
            setAskWhy(false);
            setThanked(true);
          }}
        />
      ) : null}
      {thanked ? (
        <p className="pl-[31px] text-[11px] text-muted-foreground">Thanks, that helps us improve.</p>
      ) : null}
    </div>
  );
}

function StreamingTurn({ text, tools }: { text: string; tools: ToolCall[] }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-px grid size-[22px] shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
        <FileText className="size-[13px]" />
      </div>
      <div className="min-w-0 flex-1">
        {/* Until the first token the steps end on a live status row, so the
            gap after the last tool never reads as a stalled turn. */}
        <ActivitySteps steps={tools} waiting={text === ''} />
        {text === '' ? null : (
          <>
            <Markdown source={text} className="inline" />
            <span
              className="ml-px inline-block h-[1em] w-0.5 -translate-y-[0.16em] animate-pulse bg-[var(--assistant-accent)] align-baseline"
              aria-hidden="true"
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pending attachments
 * ------------------------------------------------------------------ */

function AttachmentTray({
  attachments,
  onRemove,
}: {
  attachments: PendingAttachment[];
  onRemove: (id: string) => void;
}) {
  if (attachments.length === 0) return null;

  return (
    <div className="mb-1.5 flex flex-wrap gap-1.5">
      {attachments.map((attachment) => (
        <div key={attachment.id} className="group/thumb relative">
          {attachment.mimeType.startsWith('image/') ? (
            <img
              src={attachment.dataUrl}
              alt={attachment.name}
              title={`${attachment.name} · ${formatBytes(attachment.size)}`}
              className="size-14 rounded-md border border-border object-cover"
            />
          ) : (
            <div
              title={`${attachment.name} · ${formatBytes(attachment.size)}`}
              className="flex size-14 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/40 px-1"
            >
              <FileIcon className="size-4 text-muted-foreground" />
              <span className="max-w-full truncate text-[9px] font-medium text-muted-foreground">
                {labelForMimeType(attachment.mimeType)}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            aria-label={`Remove ${attachment.name}`}
            className="absolute -right-1.5 -top-1.5 grid size-[18px] place-items-center rounded-full border border-border bg-background text-foreground/70 shadow-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Empty state
 * ------------------------------------------------------------------ */

/**
 * The greeting and starter prompts come from config/assistant.php — they are
 * the first thing a user reads, and what they should say depends on the
 * documents that install actually handles.
 */
function EmptyState({
  greeting,
  subtitle,
  starters,
  onPick,
}: {
  greeting: string;
  subtitle: string;
  starters: string[];
  onPick: (prompt: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3.5 px-0.5 pb-2 pt-5">
      {greeting ? (
        <h2 className="text-balance text-base font-semibold tracking-tight">{greeting}</h2>
      ) : null}

      {subtitle ? <p className="text-[13px] text-muted-foreground">{subtitle}</p> : null}

      {starters.length > 0 ? (
        <div className="mt-0.5 flex flex-col gap-1.5">
          {starters.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => onPick(prompt)}
              className="flex w-full items-center gap-2.5 rounded-md border border-border bg-background px-2.5 py-2 text-left text-[12.5px] text-foreground/80 transition-colors hover:border-[var(--assistant-accent)] hover:bg-[var(--assistant-accent-soft)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MessageSquare className="size-3.5 shrink-0 text-muted-foreground" />
              {prompt}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * History
 * ------------------------------------------------------------------ */

/**
 * The user's past conversations, newest activity first. Opening one that is
 * unfinished continues it; a finished one opens read-only.
 */
function HistoryPanel({
  conversationsUrl,
  csrfToken,
  currentId,
  onOpen,
  onDeleted,
}: {
  conversationsUrl: string;
  csrfToken: string;
  currentId: string | null;
  onOpen: (id: string) => void;
  onDeleted: (id: string) => void;
}) {
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listConversations(conversationsUrl, page);
        setItems((prev) => (page === 1 ? result.data : [...prev, ...result.data]));
        setNextPage(result.next_page);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Could not load your conversations.');
      } finally {
        setLoading(false);
      }
    },
    [conversationsUrl],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const remove = useCallback(
    async (item: ConversationSummary) => {
      if (!window.confirm(`Delete "${item.title}"? Its messages and files are removed for good.`)) return;
      try {
        await deleteConversation(conversationsUrl, csrfToken, item.id);
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        onDeleted(item.id);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Could not delete that conversation.');
      }
    },
    [conversationsUrl, csrfToken, onDeleted],
  );

  return (
    <div className="flex flex-col gap-1 pb-2">
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(
            'group/item flex items-center gap-1 rounded-md border transition-colors',
            item.id === currentId
              ? 'border-[var(--assistant-accent)] bg-[var(--assistant-accent-soft)]'
              : 'border-transparent hover:bg-muted',
          )}
        >
          <button
            type="button"
            onClick={() => onOpen(item.id)}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {item.completed ? (
              <Check className="size-3.5 shrink-0 text-emerald-600" aria-label="Finished" />
            ) : (
              <MessageSquare className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 flex-1 truncate text-[12.5px]">{item.title}</span>
            <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground">
              {formatWhen(item.last_message_at)}
            </span>
          </button>
          <button
            type="button"
            onClick={() => void remove(item)}
            title="Delete conversation"
            aria-label={`Delete ${item.title}`}
            className="mr-1 grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/item:opacity-100"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}

      {!loading && items.length === 0 && error === null ? (
        <p className="px-1 pt-4 text-center text-[12.5px] text-muted-foreground">No conversations yet.</p>
      ) : null}

      {error ? <p className="px-1 pt-2 text-[12px] text-destructive">{error}</p> : null}

      {loading ? (
        <div className="grid place-items-center py-3">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : nextPage !== null && nextPage > 1 ? (
        <button
          type="button"
          onClick={() => void load(nextPage)}
          className="mx-auto mt-1 rounded px-2 py-1 text-[11.5px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Show older
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Dock
 * ------------------------------------------------------------------ */

export function ChatDock({ config }: { config: AssistantConfig }) {
  const [open, setOpen] = useState(config.openOnLoad);
  const [draft, setDraft] = useState('');
  const [atBottom, setAtBottom] = useState(true);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // A launcher icon that fails to load (e.g. assets published before the icon
  // shipped) falls back to the built-in glyph instead of a broken image.
  const [launcherIconFailed, setLauncherIconFailed] = useState(false);

  const narrow = useIsNarrow();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // dragenter/dragleave fire on every child element a drag crosses, not just
  // the panel's own boundary — a plain boolean flickers off and on as the
  // pointer passes over messages or the composer. Counting enter/leave pairs
  // instead only reaches zero once the drag has actually left the panel.
  const dragDepthRef = useRef(0);

  const chat = useChat({
    chatUrl: config.chatUrl,
    conversationsUrl: config.conversationsUrl,
    csrfToken: config.csrfToken,
    stream: config.stream,
    maxMessages: config.maxMessages,
    maxMessageChars: config.maxMessageChars,
    maxAttachmentsPerMessage: config.maxAttachmentsPerMessage,
  });

  const health = useHealth(config.healthUrl, open);
  const { recheck } = health;

  // A failed turn may mean the API went away between polls — find out now
  // rather than showing "Connected" next to the error for up to 30 s.
  useEffect(() => {
    if (chat.error) void recheck();
  }, [chat.error, recheck]);

  const attachmentsEnabled = config.maxAttachmentsPerMessage > 0 && config.allowedAttachmentTypes.length > 0;
  const acceptAttr = config.allowedAttachmentTypes.join(',');

  /* ---- attachments ---- */
  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const incoming = Array.from(files);
      if (incoming.length === 0) return;

      setAttachError(null);

      const accepted: File[] = [];
      let rejectionReason: string | null = null;

      for (const file of incoming) {
        if (!config.allowedAttachmentTypes.includes(file.type)) {
          rejectionReason = `"${file.name}" isn't a supported file type.`;
          continue;
        }
        if (file.size > config.maxAttachmentBytes) {
          rejectionReason = `"${file.name}" is larger than ${formatBytes(config.maxAttachmentBytes)}.`;
          continue;
        }
        accepted.push(file);
      }

      const room = config.maxAttachmentsPerMessage - attachments.length;
      const overflow = accepted.length > room;
      const toAdd = accepted.slice(0, Math.max(0, room));

      if (rejectionReason) setAttachError(rejectionReason);
      else if (overflow) {
        setAttachError(`Only ${config.maxAttachmentsPerMessage} attachments are allowed per message.`);
      }

      if (toAdd.length === 0) return;

      const encoded = await Promise.all(
        toAdd.map(async (file) => ({
          id: nextAttachmentId(),
          name: file.name,
          size: file.size,
          mimeType: file.type,
          dataUrl: await readAsDataUrl(file),
        })),
      );

      setAttachments((prev) => [...prev, ...encoded]);
    },
    [
      attachments.length,
      config.allowedAttachmentTypes,
      config.maxAttachmentBytes,
      config.maxAttachmentsPerMessage,
    ],
  );

  const onFilesSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) void addFiles(event.target.files);
      event.target.value = '';
    },
    [addFiles],
  );

  /* ---- drag and drop ----
   * A drop target must call preventDefault() on both dragover and drop, or
   * the browser's default action wins — for a dropped file that means
   * navigating the page to it, which looked like "nothing happens" followed
   * by leaving the app. Filtering on dataTransfer.types (rather than reading
   * .files, which is empty until the drop itself) means a drag carrying only
   * text — e.g. selecting a message and dragging it — passes through
   * untouched instead of prompting an attach affordance for it.
   */
  const isFileDrag = useCallback((event: React.DragEvent) => {
    return Array.from(event.dataTransfer.types).includes('Files');
  }, []);

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingFile(true);
    },
    [isFileDrag],
  );

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
    },
    [isFileDrag],
  );

  const onDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setIsDraggingFile(false);
    },
    [isFileDrag],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingFile(false);
      if (attachmentsEnabled) void addFiles(event.dataTransfer.files);
    },
    [addFiles, attachmentsEnabled, isFileDrag],
  );

  const { size, resizing, expanded, toggleExpanded, handleProps } = useDockSize(
    config.position,
    { width: config.width, height: config.height },
    config.offset,
    config.resizable,
  );

  /* ---- scrolling ---- */
  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 90;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Follow the stream only while the reader is already at the bottom, so
  // scrolling up to re-read something isn't yanked away mid-reply.
  useLayoutEffect(() => {
    if (atBottom) scrollToBottom();
  }, [chat.messages, chat.pending, chat.pendingTools, atBottom, scrollToBottom]);

  const onScroll = useCallback(() => setAtBottom(isNearBottom()), [isNearBottom]);

  /* ---- open / close ---- */
  const close = useCallback(() => {
    setOpen(false);
    launcherRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    // Listening on the document catches Escape regardless of shadow-DOM focus.
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 180);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  /* ---- composer ---- */
  const autoGrow = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 152)}px`;
  }, []);

  const submit = useCallback(
    (text: string) => {
      if (text.trim() === '' && attachments.length === 0) return;

      chat.send(text, attachments);
      setShowHistory(false);
      setDraft('');
      setAttachments([]);
      setAttachError(null);
      setAtBottom(true);
      window.requestAnimationFrame(autoGrow);
    },
    [attachments, autoGrow, chat],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (!chat.busy && !chat.completed && !chat.full) submit(draft);
      }
    },
    [chat.busy, chat.completed, chat.full, draft, submit],
  );

  const startNew = useCallback(() => {
    chat.reset();
    setShowHistory(false);
  }, [chat]);

  const openConversation = useCallback(
    (id: string) => {
      setShowHistory(false);
      setAtBottom(true);
      if (id !== chat.conversationId || chat.messages.length === 0) void chat.open(id);
    },
    [chat],
  );

  const onConversationDeleted = useCallback(
    (id: string) => {
      if (id === chat.conversationId) chat.reset();
    },
    [chat],
  );

  // The document that ended the conversation, for the closing notice's link.
  // Whatever kind it was, the API reports it as {id, document_no, url}; the
  // number's prefix and the assistant's reply say whether it was a sales
  // order, a purchase order or something else.
  const completingMessage = chat.completed
    ? chat.messages.find((m) => m.role === 'assistant' && m.completed)
    : undefined;
  const completedDocument: { number: string; url?: string } | null =
    completingMessage?.document?.document_no
      ? {
          number: completingMessage.document.document_no,
          url: completingMessage.document.url,
        }
      : null;

  const panelStyle: React.CSSProperties = narrow
    ? { position: 'fixed', inset: 0 }
    : { ...anchorStyle(config.position, config.offset), width: size.width, height: size.height };

  const origin = config.position
    .split('-')
    .reverse()
    .join(' ') as React.CSSProperties['transformOrigin'];

  const showEmpty = chat.messages.length === 0 && chat.pending === null && !chat.loading;

  return (
    <div style={accentVars(config.accent) as React.CSSProperties}>
      {/* Launcher */}
      {config.launcherIcon && !launcherIconFailed ? (
        // Mascot launcher: the image is the button. The label moves into a
        // hover/focus bubble on the page-facing side, and stays the
        // accessible name either way.
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={config.launcherLabel}
          style={anchorStyle(config.position, narrow ? Math.min(config.offset, 14) : config.offset)}
          className={cn(
            'group z-40 size-[76px] rounded-full p-0 transition-all duration-200',
            'animate-in fade-in zoom-in-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            open && 'pointer-events-none scale-90 opacity-0',
          )}
        >
          {/* Ground shadow — shrinks as the mascot floats up. */}
          <span
            aria-hidden="true"
            className="absolute bottom-0.5 left-1/2 h-2 w-11 -translate-x-1/2 rounded-full bg-black/20 blur-[3px] motion-safe:animate-assistant-shadow"
          />
          {/* Float lives on the wrapper so it does not fight the hover transform on the image. */}
          <span className="block size-full motion-safe:animate-assistant-float">
            <img
              src={config.launcherIcon}
              alt=""
              draggable={false}
              onError={() => setLauncherIconFailed(true)}
              className={cn(
                'pointer-events-none size-full select-none object-contain drop-shadow-[0_6px_10px_rgba(0,0,0,0.18)]',
                'transition-transform duration-300 ease-[cubic-bezier(.34,1.56,.64,1)]',
                'group-hover:-rotate-6 group-hover:scale-110 group-focus-visible:scale-110 group-active:scale-95',
              )}
            />
          </span>
          {config.launcherLabel ? (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[var(--assistant-accent)] px-3 py-1.5 text-[13px] font-medium text-[var(--assistant-accent-contrast)] shadow-lg',
                'scale-90 opacity-0 transition-all duration-200',
                'group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100',
                config.position.includes('right')
                  ? 'right-full mr-1.5 origin-right'
                  : 'left-full ml-1.5 origin-left',
              )}
            >
              {config.launcherLabel}
            </span>
          ) : null}
        </button>
      ) : (
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={config.launcherLabel}
          style={anchorStyle(config.position, narrow ? Math.min(config.offset, 14) : config.offset)}
          className={cn(
            'z-40 flex items-center gap-2.5 rounded-full bg-[var(--assistant-accent)] font-medium text-[var(--assistant-accent-contrast)] shadow-lg transition-all duration-200',
            'hover:bg-[var(--assistant-accent-hover)] hover:-translate-y-px',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            config.launcherStyle === 'icon'
              ? 'size-[52px] justify-center p-0'
              : 'h-[46px] pl-[15px] pr-[17px] text-[13.5px]',
            open && 'pointer-events-none scale-90 opacity-0',
          )}
        >
          <MessageSquare className={config.launcherStyle === 'icon' ? 'size-[21px]' : 'size-[17px]'} />
          {config.launcherStyle === 'label' ? <span>{config.launcherLabel}</span> : null}
        </button>
      )}

      {/* Panel */}
      <section
        aria-label={config.title}
        style={{ ...panelStyle, transformOrigin: origin }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'z-50 grid grid-rows-[auto_1fr_auto] overflow-hidden border border-border bg-background shadow-2xl',
          narrow ? 'rounded-none border-0' : 'rounded-[14px]',
          resizing ? 'select-none' : 'transition-all [transition-duration:260ms]',
          !open &&
            cn(
              'pointer-events-none scale-95 opacity-0',
              config.position.includes('bottom') ? 'translate-y-2.5' : '-translate-y-2.5',
            ),
        )}
      >
        {isDraggingFile && attachmentsEnabled ? (
          <div className="pointer-events-none absolute inset-0 z-[60] grid place-items-center gap-2 border-2 border-dashed border-[var(--assistant-accent)] bg-[var(--assistant-accent-soft)]/95 backdrop-blur-[1px]">
            <Paperclip className="size-5 text-[var(--assistant-accent)]" />
            <p className="text-[13px] font-medium text-foreground">Drop files to attach</p>
          </div>
        ) : null}

        {config.resizable && !narrow ? (
          <Grips position={config.position} handleProps={handleProps} />
        ) : null}

        {/* Header */}
        <header className="flex items-center gap-2.5 border-b border-border py-2.5 pl-3.5 pr-2.5">
          <div className="flex min-w-0 flex-col gap-px">
            <div className="flex min-w-0 items-center gap-1.5">
              <b className="truncate text-[13.5px] font-semibold tracking-tight">{config.title}</b>
              {config.badge ? (
                <span className="shrink-0 rounded-full border border-[var(--assistant-accent)]/30 bg-[var(--assistant-accent-soft)] px-1.5 py-px text-[10px] font-medium uppercase leading-[14px] tracking-wide text-[var(--assistant-accent)]">
                  {config.badge}
                </span>
              ) : null}
            </div>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  chat.busy
                    ? 'bg-[var(--assistant-accent)]'
                    : health.status === 'offline'
                      ? 'bg-destructive'
                      : health.status === 'checking'
                        ? 'animate-pulse bg-muted-foreground'
                        : 'bg-emerald-600',
                )}
              />
              {chat.busy
                ? 'Generating…'
                : chat.completed
                  ? 'Finished'
                  : health.status === 'offline'
                    ? 'Unavailable'
                    : health.status === 'checking'
                      ? 'Connecting…'
                      : 'Connected'}
            </span>
          </div>

          <div className="ml-auto flex items-center">
            {config.conversationsUrl ? (
              <button
                type="button"
                onClick={() => setShowHistory((shown) => !shown)}
                title={showHistory ? 'Back to conversation' : 'Your conversations'}
                aria-label={showHistory ? 'Back to conversation' : 'Your conversations'}
                aria-pressed={showHistory}
                className={cn(
                  'grid size-[30px] place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  showHistory && 'bg-muted text-foreground',
                )}
              >
                <History className="size-4" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={startNew}
              title="New conversation"
              aria-label="New conversation"
              className="grid size-[30px] place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="size-4" />
            </button>

            {config.resizable && !narrow ? (
              <button
                type="button"
                onClick={toggleExpanded}
                title={expanded ? 'Shrink panel' : 'Expand panel'}
                aria-label={expanded ? 'Shrink panel' : 'Expand panel'}
                className="grid size-[30px] place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              </button>
            ) : null}

            <button
              type="button"
              onClick={close}
              title="Close"
              aria-label="Close assistant"
              className="grid size-[30px] place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          </div>
        </header>

        {/* Transcript */}
        <div className="relative min-h-0">
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="ocr-widget-scroll flex h-full flex-col gap-[18px] overflow-y-auto overflow-x-hidden px-3.5 pb-2 pt-4"
            aria-live="polite"
            aria-atomic="false"
          >
            {showHistory ? (
              <HistoryPanel
                conversationsUrl={config.conversationsUrl}
                csrfToken={config.csrfToken}
                currentId={chat.conversationId}
                onOpen={openConversation}
                onDeleted={onConversationDeleted}
              />
            ) : null}

            {!showHistory && chat.loading ? (
              <div className="grid flex-1 place-items-center">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : null}

            {!showHistory && showEmpty ? (
              <EmptyState
                greeting={config.greeting}
                subtitle={config.greetingSubtitle}
                starters={config.starters}
                onPick={submit}
              />
            ) : null}

            {!showHistory && chat.messages.map((message, index) =>
              message.role === 'user' ? (
                <UserTurn key={message.id} message={message} />
              ) : (
                <AssistantTurn
                  key={message.id}
                  message={message}
                  onRetry={chat.retry}
                  canRetry={!chat.busy && !chat.completed && index === chat.messages.length - 1}
                  isLast={index === chat.messages.length - 1}
                  onRate={
                    config.feedback && message.serverId !== undefined
                      ? (rating, reasons, comment) => void chat.rate(message.id, rating, reasons, comment)
                      : undefined
                  }
                />
              ),
            )}

            {!showHistory && chat.pending !== null ? (
              <StreamingTurn text={chat.pending} tools={chat.pendingTools} />
            ) : null}

            {!showHistory && chat.error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
                <p className="text-[12.5px] text-foreground">{chat.error.message}</p>
                {chat.error.requestId ? (
                  <p className="mt-1 font-mono text-[10.5px] tabular-nums text-muted-foreground">
                    {chat.error.requestId}
                  </p>
                ) : null}
                <div className="mt-1.5 flex gap-1.5">
                  {chat.unanswered ? (
                    <button
                      type="button"
                      onClick={chat.retry}
                      className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      Try again
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={chat.dismissError}
                    className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}

            {/* No error to explain it — typically after a reload, since errors
                are not stored — but the last message still has no reply. */}
            {!showHistory && !chat.error && chat.unanswered && !chat.completed ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2">
                <p className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">
                  {health.status === 'offline'
                    ? "No reply yet — the assistant can't be reached right now."
                    : 'This message didn’t get a reply.'}
                </p>
                <button
                  type="button"
                  onClick={chat.retry}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Try again
                </button>
              </div>
            ) : null}
          </div>

          {!atBottom && !showHistory ? (
            <button
              type="button"
              onClick={() => {
                setAtBottom(true);
                scrollToBottom();
              }}
              className="absolute bottom-3 left-1/2 z-40 flex h-7 -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background px-2.5 text-[11.5px] text-foreground/80 shadow-md transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ArrowDown className="size-3" />
              Latest
            </button>
          ) : null}
        </div>

        {/* Composer, or the closing notice once the conversation is done */}
        {chat.completed ? (
          <div className="flex items-center gap-2.5 border-t border-border px-3.5 py-3">
            <div className="grid size-[22px] shrink-0 place-items-center rounded-md bg-emerald-600 text-white">
              <Check className="size-[13px]" />
            </div>
            <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-foreground">
              {completedDocument ? (
                <>
                  {completedDocument.url ? (
                    <a
                      href={completedDocument.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium underline decoration-muted-foreground/50 underline-offset-2 hover:decoration-current"
                    >
                      {completedDocument.number}
                    </a>
                  ) : (
                    <span className="font-medium">{completedDocument.number}</span>
                  )}{' '}
                  is saved. This conversation is finished.
                </>
              ) : (
                'This conversation is finished.'
              )}
            </p>
            <button
              type="button"
              onClick={startNew}
              className="shrink-0 rounded-md bg-[var(--assistant-accent)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--assistant-accent-contrast)] transition-colors hover:bg-[var(--assistant-accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              New conversation
            </button>
          </div>
        ) : chat.full && !chat.busy ? (
          <div className="flex items-center gap-2.5 border-t border-border px-3.5 py-3">
            <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-foreground">
              This conversation has reached its length limit.
            </p>
            <button
              type="button"
              onClick={startNew}
              className="shrink-0 rounded-md bg-[var(--assistant-accent)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--assistant-accent-contrast)] transition-colors hover:bg-[var(--assistant-accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              New conversation
            </button>
          </div>
        ) : (
        <div className="border-t border-border px-3 pb-2.5 pt-2.5">
          <div className="rounded-md border border-input bg-background px-2.5 pb-1.5 pt-1.5 transition-shadow focus-within:border-[var(--assistant-accent)] focus-within:shadow-[0_0_0_3px_var(--assistant-accent-soft)]">
            <AttachmentTray attachments={attachments} onRemove={removeAttachment} />

            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                autoGrow();
              }}
              onKeyDown={onKeyDown}
              placeholder="Ask about a document, or paste an extracted value…"
              className="block max-h-[152px] w-full resize-none border-0 bg-transparent pb-1 pt-0.5 text-[13.5px] leading-normal placeholder:text-muted-foreground focus:outline-none"
            />
            <div className="flex items-center gap-1">
              {attachmentsEnabled ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptAttr}
                    multiple
                    hidden
                    onChange={onFilesSelected}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= config.maxAttachmentsPerMessage}
                    title="Attach a file"
                    aria-label="Attach a file"
                    className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Paperclip className="size-4" />
                  </button>
                </>
              ) : null}
              <span className="ml-auto" />
              <button
                type="button"
                onClick={() => (chat.busy ? chat.stop() : submit(draft))}
                disabled={!chat.busy && draft.trim() === '' && attachments.length === 0}
                aria-label={chat.busy ? 'Stop generating' : 'Send message'}
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  chat.busy
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-[var(--assistant-accent)] text-[var(--assistant-accent-contrast)] hover:bg-[var(--assistant-accent-hover)] disabled:pointer-events-none disabled:opacity-40',
                )}
              >
                {chat.busy ? (
                  <Square className="size-3 fill-current" />
                ) : (
                  <ArrowUp className="size-[15px]" />
                )}
              </button>
            </div>
          </div>

          {attachError ? (
            <p className="px-0.5 pt-1 text-[11px] text-destructive">{attachError}</p>
          ) : null}

          <div className="flex items-center gap-1.5 px-0.5 pt-1.5 text-[10.5px] text-muted-foreground">
            <kbd className="rounded border border-border border-b-2 px-1 font-mono text-[9.5px]">
              Enter
            </kbd>
            to send
            <span className="ml-auto font-mono tabular-nums">
              {draft.length > 0 ? `${draft.length.toLocaleString()} / ${config.maxMessageChars.toLocaleString()}` : null}
            </span>
          </div>

          {config.disclaimer ? (
            <p className="px-0.5 pt-1 text-center text-[10.5px] text-muted-foreground">
              {config.disclaimer}
            </p>
          ) : null}
        </div>
        )}
      </section>
    </div>
  );
}
